import { Add, Cancel, Close } from "@mui/icons-material";
import {
  Autocomplete,
  FormControl,
  IconButton,
  MenuItem,
  Modal,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  ChangeEvent,
  FormEvent,
  SyntheticEvent,
  useCallback,
  useState,
} from "react";
import { useCreateMedicationMutation } from "src/api/services/medicationsApi";
import { useGetPharmaciesQuery } from "src/api/services/pharmacyApi";
import {
  useCreateTreatmentMutation,
  useGetTreatmentsQuery,
} from "src/api/services/treatmentsApi";
import {
  MedicationForm,
  MedicationFormEnums,
  Pharmacy,
  Treatment,
} from "src/api/types";
import LoadingButton from "src/components/Buttons/LoadingButton";
import { useSentryLogger } from "src/hooks/useSentryLogger";
import { desktopSx } from "src/utils/styles/modalStyles";

interface Props {
  open: boolean;
  onClose: () => void;
}

const CreateMedicationModal = ({ onClose, open }: Props) => {
  const { data: pharmacies = [] } = useGetPharmaciesQuery();
  const [createMedication] = useCreateMedicationMutation();
  const [createTreatment] = useCreateTreatmentMutation();
  const { data: treatments = [] } = useGetTreatmentsQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [name, setName] = useState<string>("");
  const [dosage, setDosage] = useState<string>("");
  const [strength, setStrength] = useState<string>("");
  const [form, setForm] = useState<MedicationForm | null>(null);
  const [dispenseUnit, setDispenseUnit] = useState<string>("");
  const [dispenseQuantity, setDispenseQuantity] = useState<number>(0);
  const [pharmacy, setPharmacy] = useState<Pharmacy | null>(null);
  const [drugId, setDrugId] = useState<string>("");
  const [ndc, setNdc] = useState<string | null>(null);
  const [treatment, setTreatment] = useState<Treatment | null>(null);
  const [displayInput, setDisplayInput] = useState<boolean>(false);
  const [newTreatment, setNewTreatment] = useState<string>("");
  const errLog = useSentryLogger("error");

  const handleName = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
  }, []);

  const handleDosage = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setDosage(e.target.value);
  }, []);

  const handleStrength = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setStrength(e.target.value);
  }, []);

  const handleForm = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value as MedicationForm;

    setForm(value);
  }, []);

  const handleDispenseUnit = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setDispenseUnit(e.target.value);
  }, []);

  const handleDispenseQuantity = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setDispenseQuantity(Number(e.target.value));
    },
    [],
  );

  const handlePharmacy = useCallback(
    (_: SyntheticEvent<Element | Event>, value: Pharmacy | null) => {
      setPharmacy(value);
    },
    [],
  );

  const handleTreatment = useCallback(
    (_: SyntheticEvent<Element | Event>, value: Treatment | null) => {
      setTreatment(value);
    },
    [],
  );

  const handleDrugId = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setDrugId(e.target.value);
  }, []);

  const handleNdc = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.length === 0) {
      setNdc(null);
      return;
    }

    setNdc(e.target.value);
  }, []);

  const handleNewTreatment = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setNewTreatment(e.target.value);
  }, []);

  const handleSubmitTreatment = useCallback(async () => {
    if (newTreatment.length === 0) return;

    try {
      const res = await createTreatment({ name: newTreatment }).unwrap();

      if (res != null) {
        setTreatment(res);
        setDisplayInput(false);
      }
    } catch (error) {
      errLog("Could not create new treatment", error);
    }
  }, [createTreatment, errLog, newTreatment]);

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      if (form == null || pharmacy == null || treatment == null) return;
      setLoading(true);

      try {
        await createMedication({
          dispense_quantity: dispenseQuantity,
          dispense_unit: dispenseUnit,
          dosage,
          name,
          strength,
          form,
          drug_id: drugId,
          pharmacy_id: pharmacy?.id,
          treatment_id: treatment.id,
          ndc,
        }).unwrap();

        setName("");
        setDispenseQuantity(0);
        setDispenseUnit("");
        setDosage("");
        setStrength("");
        setPharmacy(null);
        setForm(null);
        setTreatment(null);
        setDrugId("");
        setNdc(null);
        onClose();
      } catch (error) {
        errLog("Unable to create medication", error);
      } finally {
        setLoading(false);
      }
    },
    [
      createMedication,
      dispenseQuantity,
      dispenseUnit,
      dosage,
      drugId,
      errLog,
      form,
      name,
      ndc,
      onClose,
      pharmacy,
      strength,
      treatment,
    ],
  );

  return (
    <Modal open={open}>
      <Stack
        sx={desktopSx}
        gap={2}
        component="form"
        maxWidth={400}
        onSubmit={handleSubmit}
      >
        <Stack
          direction={"row"}
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h5" component="h5" fontWeight={700}>
            Add Medication
          </Typography>
          <IconButton onClick={onClose} sx={{ p: 0 }}>
            <Close fontSize="medium" />
          </IconButton>
        </Stack>
        <Stack gap={1.5}>
          <FormControl>
            <Autocomplete
              options={treatments}
              getOptionLabel={(treatment) => treatment.name}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Treatment"
                  required
                  helperText={
                    <Typography variant="caption" fontSize={"0.8rem"}>
                      Can't find the desired treatment.{" "}
                      <a
                        style={{ cursor: "pointer" }}
                        onClick={() => setDisplayInput(true)}
                      >
                        Add here
                      </a>
                    </Typography>
                  }
                />
              )}
              value={treatment}
              onChange={handleTreatment}
            />
          </FormControl>
          {displayInput ? (
            <Stack direction={"row"}>
              <FormControl fullWidth>
                <TextField
                  fullWidth
                  label="New treatment"
                  required
                  value={newTreatment}
                  onChange={handleNewTreatment}
                />
              </FormControl>
              <IconButton onClick={handleSubmitTreatment}>
                <Add />
              </IconButton>
              <IconButton onClick={() => setDisplayInput(false)}>
                <Cancel />
              </IconButton>
            </Stack>
          ) : null}
          <FormControl>
            <TextField
              label="Name"
              required
              value={name}
              onChange={handleName}
            />
          </FormControl>
          <FormControl>
            <TextField
              label="Dosage"
              required
              value={dosage}
              onChange={handleDosage}
            />
          </FormControl>
          <FormControl>
            <TextField
              label="Strength"
              required
              value={strength}
              onChange={handleStrength}
              helperText="*Medication formula"
            />
          </FormControl>
          <FormControl>
            <TextField
              label="Form"
              required
              select
              value={form}
              onChange={handleForm}
            >
              {MedicationFormEnums.map((form) => (
                <MenuItem value={form} key={form}>
                  {form}
                </MenuItem>
              ))}
            </TextField>
          </FormControl>
          <FormControl>
            <TextField
              label="Dispense unit"
              required
              value={dispenseUnit}
              onChange={handleDispenseUnit}
            />
          </FormControl>
          <FormControl>
            <TextField
              label="Dispense quantity"
              required
              type="number"
              value={dispenseQuantity}
              onChange={handleDispenseQuantity}
            />
          </FormControl>
          <FormControl>
            <Autocomplete
              options={pharmacies}
              getOptionLabel={(pharmacy) => pharmacy.name}
              renderInput={(params) => (
                <TextField {...params} label="Pharmacy" required />
              )}
              value={pharmacy}
              onChange={handlePharmacy}
            />
          </FormControl>
          <FormControl>
            <TextField
              label="Drug ID"
              required
              value={drugId}
              onChange={handleDrugId}
            />
          </FormControl>
          <FormControl>
            <TextField label="NDC" value={ndc} onChange={handleNdc} />
          </FormControl>
          <LoadingButton type="submit" loading={loading}>
            Submit
          </LoadingButton>
        </Stack>
      </Stack>
    </Modal>
  );
};

export default CreateMedicationModal;
